import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import labelsMixin from '../../mixins/labels.js';
import PickerPopover from '../../utils/picker-popover.vue.js';
import ColorPickerInput from './common/color-picker-input.vue.js';
import ColorPickerSaturation from './common/color-picker-saturation.vue.js';
import ColorPickerSlider from './common/color-picker-slider.vue.js';
import ColorPickerPresets from './common/pendo-color-picker-presets.vue.js';
import ColorPickerSwatch from './common/color-picker-swatch.vue.js';
import FocusTrap from '../../utils/focus-trap.js';
import { DEFAULT_PRESETS } from './utils.js';
import { getColor, toRgbString, isValidHex, normalizeHex } from '../../utils/color.js';

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }
  if (!value || !value.then) {
    value = Promise.resolve(value);
  }
  return then ? value.then(then) : value;
}
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
var script = {
  name: 'PendoColorPicker',
  components: {
    ColorPickerSwatch,
    ColorPickerSlider,
    ColorPickerInput,
    ColorPickerSaturation,
    ColorPickerPresets,
    PickerPopover,
    FocusTrap
  },
  mixins: [labelsMixin],
  props: {
    initialColor: {
      type: [String, Object],
      default: '#000000'
    },
    value: {
      type: [String, Object],
      default: null
    },
    valueKey: {
      type: String,
      default: null,
      validator: valueKey => ['r', 'g', 'b', 'alpha', 'hex', 'hex8', 'hsv', 'hsl'].includes(valueKey)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disablePresets: {
      type: Boolean,
      default: false
    },
    disableAlpha: {
      type: Boolean,
      default: false
    },
    disableFields: {
      type: Boolean,
      default: false
    },
    presets: {
      type: Array,
      default: () => DEFAULT_PRESETS
    },
    triggerWidth: {
      type: [Number, String],
      default: 132
    }
  },
  data() {
    return {
      isFocused: false,
      isVisible: false,
      internalColor: null,
      isFocusTrapActive: false
    };
  },
  computed: {
    color: {
      get() {
        return getColor(this.value ? this.value : this.internalColor);
      },
      set(color) {
        this.internalColor = color;
        this.$emit('input', color[this.valueKey] || color);
        this.$emit('change', color[this.valueKey] || color);
      }
    },
    pickerTriggerWidth() {
      if (typeof this.triggerWidth === 'string') {
        return this.triggerWidth;
      }
      return `${this.triggerWidth}px`;
    },
    isActive() {
      return this.isFocused || this.isVisible;
    },
    activeColor() {
      return toRgbString(this.color);
    }
  },
  watch: {
    isActive(val) {
      this.$emit('active', val);
    }
  },
  created() {
    this.color = getColor(this.value ? this.value : this.initialColor);
  },
  methods: {
    updateColor(data) {
      this.color = getColor(data);
    },
    handlePresetClick(color) {
      this.updateColor({
        hex: color
      });
    },
    handleChildChange(data) {
      this.updateColor(_objectSpread2({
        alpha: this.color.alpha
      }, data));
    },
    handleInputChange(data) {
      if (!data) {
        return;
      }
      const {
        hex,
        r,
        g,
        b,
        alpha
      } = data;
      if (hex && isValidHex(hex)) {
        this.updateColor({
          hex: normalizeHex(hex)
        });
      } else if (r !== undefined || g !== undefined || b !== undefined || alpha !== undefined) {
        this.updateColor({
          r: r !== undefined ? r : this.color.r,
          g: g !== undefined ? g : this.color.g,
          b: b !== undefined ? b : this.color.b,
          alpha: alpha !== undefined ? alpha / 100 : this.color.alpha
        });
      }
    },
    handleInputFocus() {
      this.isFocused = true;
    },
    handleInputBlur() {
      this.isFocused = false;
    },
    toggleColorPicker() {
      if (this.isVisible) {
        this.hideColorPicker();
      } else {
        this.showColorPicker();
      }
    },
    onEscKeydown() {
      this.hideColorPicker();
    },
    showColorPicker: _async(function () {
      const _this = this;
      _this.isVisible = true;
      _this.$emit('show', _this.color[_this.valueKey] || _this.color);
      return _await(_this.$nextTick(), function () {
        _this.isFocusTrapActive = true;
      });
    }),
    hideColorPicker() {
      this.isVisible = false;
      this.$emit('hide', this.color[this.valueKey] || this.color);
      this.isFocusTrapActive = false;
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-color-picker.vue2.js.map
